// A small generic store for local-only UI preferences, so each new one is a couple of
// lines instead of a hand-rolled copy of `session.ts`'s shape. `theme.ts` is the first
// user; anything else with a fixed set of values and no reason to live server-side
// (density, font size, whatever comes next) fits the same shape.

export function createPreference<T extends string>(
  key: string,
  values: readonly T[],
  fallback: T,
): {
  get(): T;
  set(value: T): void;
  subscribe(listener: () => void): () => void;
} {
  const storageKey = `mor.pref.${key}`;

  function readStored(): T {
    try {
      const raw = globalThis.localStorage?.getItem(storageKey);
      return (values as readonly string[]).includes(raw ?? '') ? (raw as T) : fallback;
    } catch {
      return fallback;
    }
  }

  let value: T = readStored();
  const listeners = new Set<() => void>();

  return {
    get(): T {
      return value;
    },
    set(next: T): void {
      value = next;
      try {
        globalThis.localStorage?.setItem(storageKey, next);
      } catch {
        // Preference lives for this page load only; not worth failing over.
      }
      for (const listener of listeners) {
        listener();
      }
    },
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
